import type { ReactElement, ReactNode } from 'react';
import { useDisplayTimeZone } from '@/hooks/useDisplayTimeZone';
import { ChevronDown, Github } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type {
  GithubStatusResponse,
  GithubSyncStatusResponse,
} from '@kirin/api-client/contracts/github';

import { useTimestampFormat } from '@/hooks/useTimestampFormat';

import { IntegrationCard } from './IntegrationCard';
import {
  renderConnectButton,
  renderDisconnectButton,
  renderErrorAlert,
  renderErrorBadgeSlot,
  renderMeta,
  renderRunningIndicator,
  renderStatusCopy,
  renderSyncButton,
  renderSyncNote,
  renderTryAgainButton,
  type SyncCardStatus,
  type SyncRendererConfig,
} from './syncIntegrationRenderers';

export type GithubCardStatus = SyncCardStatus;

export interface GithubIntegrationCardProps {
  status: GithubStatusResponse;
  syncStatus?: GithubSyncStatusResponse;
  onConnect?: () => void;
  onManualConnect?: () => void;
  onDisconnect?: () => void;
  onSync?: () => void;
  cardStatus?: GithubCardStatus;
  isLoading?: boolean;
  isSyncing?: boolean;
  // Button-local pending signals, separate from card-level `isLoading`
  // (data-fetch only). These drive the Connect / Disconnect button labels
  // independently of the body skeleton so a pending mutation can show
  // `Connecting...` / `Disconnecting...` without replacing the card body.
  connectIsPending?: boolean;
  disconnectIsPending?: boolean;
  error?: string;
  connectTooltip?: string;
  syncNote?: string;
  readOnly?: boolean;
}

const CFG: SyncRendererConfig = {
  testIdPrefix: 'github',
  displayName: 'GitHub',
};

function renderBody(
  cardStatus: GithubCardStatus,
  syncStatus: GithubSyncStatusResponse | undefined,
  syncNote: string | undefined,
  error: string | undefined
): ReactNode {
  const showRunning =
    cardStatus === 'connected' && syncStatus?.isRunning === true;
  const showNote = cardStatus === 'connected';
  const showError = cardStatus === 'error' && Boolean(error);
  const statusCopy = renderStatusCopy(CFG, cardStatus);

  if (!showRunning && !showNote && !showError && statusCopy === null) {
    return null;
  }

  return (
    <IntegrationCard.Body>
      {statusCopy}
      {showRunning && renderRunningIndicator(CFG, syncStatus?.isRunning)}
      {showNote && renderSyncNote(CFG, syncNote)}
      {showError && renderErrorAlert(CFG, error)}
    </IntegrationCard.Body>
  );
}

function renderManualConnectDropdown(
  onManualConnect: (() => void) | undefined,
  readOnly?: boolean
): ReactNode {
  if (!onManualConnect) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          aria-label="More connection options"
          disabled={readOnly}
        >
          <ChevronDown />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" sideOffset={6} className="w-72">
        <DropdownMenuItem
          onClick={onManualConnect}
          className="flex flex-col items-start gap-1 py-2"
        >
          <span className="text-sm font-medium leading-none">
            Connect with Installation ID
          </span>
          <span className="text-xs text-muted-foreground leading-snug">
            Already installed the app? Enter the installation ID directly.
          </span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function renderActions(
  cardStatus: GithubCardStatus,
  props: GithubIntegrationCardProps
): ReactNode {
  const {
    onConnect,
    onManualConnect,
    onDisconnect,
    onSync,
    isSyncing = false,
    connectIsPending = false,
    disconnectIsPending = false,
    connectTooltip,
    readOnly,
  } = props;
  const tooltip = connectTooltip ?? 'Connect your GitHub workspace';

  if (cardStatus === 'connected') {
    if (!onSync && !onDisconnect) return null;
    return (
      <IntegrationCard.Actions>
        {renderSyncButton(CFG, { onSync, isSyncing, readOnly })}
        {renderDisconnectButton(CFG, {
          onDisconnect,
          isSyncing,
          isPending: disconnectIsPending,
          readOnly,
        })}
      </IntegrationCard.Actions>
    );
  }

  if (cardStatus === 'error') {
    if (!onConnect && !onDisconnect) return null;
    return (
      <IntegrationCard.Actions>
        {renderTryAgainButton(CFG, onConnect, readOnly)}
        {renderDisconnectButton(CFG, {
          onDisconnect,
          isSyncing,
          isPending: disconnectIsPending,
          readOnly,
        })}
      </IntegrationCard.Actions>
    );
  }

  if (!onConnect && !onManualConnect) return null;
  return (
    <IntegrationCard.Actions>
      {renderConnectButton(CFG, {
        onConnect,
        tooltip,
        isPending: connectIsPending,
        readOnly,
      })}
      {renderManualConnectDropdown(onManualConnect, readOnly)}
    </IntegrationCard.Actions>
  );
}

export function GithubIntegrationCard(
  props: GithubIntegrationCardProps
): ReactElement {
  const { status, syncStatus } = props;
  const { value: timestampFormat } = useTimestampFormat();
  const timeZone = useDisplayTimeZone();
  const connected = status.connected === true;
  const cardStatus: GithubCardStatus =
    props.cardStatus ?? (connected ? 'connected' : 'not_connected');
  const primitiveStatus: 'connected' | 'not_configured' =
    cardStatus === 'connected' ? 'connected' : 'not_configured';

  return (
    <IntegrationCard
      icon={<Github size={20} aria-hidden />}
      title="GitHub"
      subtitle="Connect your GitHub workspace to invite organization members to Kirin"
      status={primitiveStatus}
      isLoading={props.isLoading}
    >
      {renderErrorBadgeSlot(CFG, cardStatus)}
      {renderMeta(CFG, {
        orgLabel: 'Organization',
        orgName: status.githubOrgName,
        syncLabel: 'Last sync',
        importedCount: syncStatus?.importedCount ?? status.importedCount,
        linkedCount: syncStatus?.linkedCount,
        lastSyncAt: syncStatus?.lastSyncAt,
        lastSyncStatus: syncStatus?.lastSyncStatus,
        connectedAt: status.connectedAt,
        hasSyncStatus: syncStatus !== undefined,
        timestampFormat,
        timeZone,
      })}
      {renderBody(cardStatus, syncStatus, props.syncNote, props.error)}
      {renderActions(cardStatus, props)}
    </IntegrationCard>
  );
}

GithubIntegrationCard.displayName = 'GithubIntegrationCard';
